import { useState, useEffect } from 'react';
import { AdminLayout } from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, GripVertical, Save, Loader2, Building2, Camera, Heart, Wine, Utensils, Cake, Music, PartyPopper, Coffee, Sun } from 'lucide-react';
import { toast } from 'sonner';
import { getAdminProgramme, updateProgramme } from '@/lib/api';

const ICON_OPTIONS = [
  { value: 'Building2', label: 'Mairie', icon: Building2 },
  { value: 'Camera', label: 'Photo', icon: Camera },
  { value: 'Heart', label: 'Coeur', icon: Heart },
  { value: 'Wine', label: 'Vin', icon: Wine },
  { value: 'Utensils', label: 'Repas', icon: Utensils },
  { value: 'Cake', label: 'Gâteau', icon: Cake },
  { value: 'Music', label: 'Musique', icon: Music },
  { value: 'PartyPopper', label: 'Fête', icon: PartyPopper },
  { value: 'Coffee', label: 'Café', icon: Coffee },
  { value: 'Sun', label: 'Soleil', icon: Sun },
];

const getIconComponent = (iconName) => {
  const found = ICON_OPTIONS.find(opt => opt.value === iconName);
  return found ? found.icon : PartyPopper;
};

const EventRow = ({ event, index, onUpdate, onDelete }) => {
  const IconComponent = getIconComponent(event.icon);
  
  return (
    <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200 mb-2">
      <GripVertical className="w-4 h-4 text-gray-400 cursor-move" />
      
      <Input
        data-testid={`event-time-${index}`}
        value={event.time}
        onChange={(e) => onUpdate(index, 'time', e.target.value)}
        placeholder="Heure (ex: 14h00)"
        className="w-24"
      />
      
      <Input
        data-testid={`event-title-${index}`}
        value={event.title}
        onChange={(e) => onUpdate(index, 'title', e.target.value)}
        placeholder="Titre de l'événement"
        className="flex-1"
      />
      
      <Select
        value={event.icon}
        onValueChange={(value) => onUpdate(index, 'icon', value)}
      >
        <SelectTrigger data-testid={`event-icon-${index}`} className="w-32">
          <div className="flex items-center gap-2">
            <IconComponent className="w-4 h-4 text-terracotta" />
            <SelectValue />
          </div>
        </SelectTrigger>
        <SelectContent>
          {ICON_OPTIONS.map(opt => (
            <SelectItem key={opt.value} value={opt.value}>
              <div className="flex items-center gap-2">
                <opt.icon className="w-4 h-4" />
                {opt.label}
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      
      <Button
        data-testid={`delete-event-${index}`}
        variant="ghost"
        size="sm"
        onClick={() => onDelete(index)}
        className="text-red-500 hover:text-red-700 hover:bg-red-50"
      >
        <Trash2 className="w-4 h-4" />
      </Button>
    </div>
  );
};

const AdminProgramme = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [samedi, setSamedi] = useState([]);
  const [dimanche, setDimanche] = useState([]);

  useEffect(() => {
    loadProgramme();
  }, []);

  const loadProgramme = async () => {
    try {
      const response = await getAdminProgramme();
      setSamedi(response.data.samedi || []);
      setDimanche(response.data.dimanche || []);
    } catch (error) {
      toast.error('Erreur lors du chargement du programme');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateProgramme({ samedi, dimanche });
      toast.success('Programme mis à jour !');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const updateEvent = (day, index, field, value) => {
    const setter = day === 'samedi' ? setSamedi : setDimanche;
    const events = day === 'samedi' ? [...samedi] : [...dimanche];
    events[index] = { ...events[index], [field]: value };
    setter(events);
  };

  const addEvent = (day) => {
    const newEvent = { time: '', title: '', icon: 'PartyPopper' };
    if (day === 'samedi') {
      setSamedi([...samedi, newEvent]);
    } else {
      setDimanche([...dimanche, newEvent]);
    }
  };

  const deleteEvent = (day, index) => {
    if (day === 'samedi') {
      setSamedi(samedi.filter((_, i) => i !== index));
    } else {
      setDimanche(dimanche.filter((_, i) => i !== index));
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Programme">
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-terracotta" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Programme">
      <div className="space-y-6">
        {/* Save Button */}
        <div className="flex justify-end">
          <Button
            data-testid="save-programme-btn"
            onClick={handleSave}
            disabled={saving}
            className="bg-terracotta hover:bg-terracotta/90"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Sauvegarde...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Sauvegarder
              </>
            )}
          </Button>
        </div>

        {/* Samedi */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Samedi - Programme</span>
              <Button
                data-testid="add-samedi-event"
                variant="outline"
                size="sm"
                onClick={() => addEvent('samedi')}
              >
                <Plus className="w-4 h-4 mr-1" />
                Ajouter
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {samedi.length === 0 ? (
              <p className="text-gray-500 text-center py-4">Aucun événement pour le samedi</p>
            ) : (
              samedi.map((event, index) => (
                <EventRow
                  key={index}
                  event={event}
                  index={index}
                  onUpdate={(idx, field, value) => updateEvent('samedi', idx, field, value)}
                  onDelete={(idx) => deleteEvent('samedi', idx)}
                />
              ))
            )}
          </CardContent>
        </Card>

        {/* Dimanche */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Dimanche - Programme</span>
              <Button
                data-testid="add-dimanche-event"
                variant="outline"
                size="sm"
                onClick={() => addEvent('dimanche')}
              >
                <Plus className="w-4 h-4 mr-1" />
                Ajouter
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {dimanche.length === 0 ? (
              <p className="text-gray-500 text-center py-4">Aucun événement pour le dimanche</p>
            ) : (
              dimanche.map((event, index) => (
                <EventRow
                  key={index}
                  event={event}
                  index={index}
                  onUpdate={(idx, field, value) => updateEvent('dimanche', idx, field, value)}
                  onDelete={(idx) => deleteEvent('dimanche', idx)}
                />
              ))
            )}
          </CardContent>
        </Card>

        {/* Preview hint */}
        <div className="text-center text-sm text-gray-500">
          <p>Les modifications seront visibles sur la page Programme après sauvegarde.</p>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminProgramme;
